import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, CreditCard, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const PLANS = (total, deposit, depPct) => [
  { id: "deposit", label: "Reservation Deposit", amount: deposit, note: `${depPct}% of total to secure your date`, icon: CreditCard },
  { id: "50", label: "50% Payment", amount: +(total * 0.5).toFixed(2), note: "Due two weeks before your event", icon: CalendarClock },
  { id: "100", label: "Pay in Full (100%)", amount: +total.toFixed(2), note: "Settle the entire balance now", icon: CheckCircle2 },
  { id: "split", label: "Split 50% / 50%", amount: +(total * 0.5).toFixed(2), note: "50% now (two weeks before) + 50% two days before", icon: CalendarClock },
];

export const PaymentPanel = ({ quote }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [selected, setSelected] = useState("deposit");
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(false);
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const total = quote?.estimated_total || 0;
  const depPct = pricing?.deposit_percent ?? 30;
  const deposit = +(total * depPct / 100).toFixed(2);
  const plans = PLANS(total, deposit, depPct);
  const current = plans.find((p) => p.id === selected);

  if (!user || !user.email) {
    return (
      <div className="border border-gold/40 bg-gold/[0.05] p-8 text-center" data-testid="payment-login-prompt">
        <h3 className="text-2xl font-light text-white mb-3">Reserve your date</h3>
        <p className="text-white/60 mb-6 max-w-md mx-auto">Create an account or sign in to pay your deposit securely with PayPal and lock in your event date.</p>
        <button onClick={() => { localStorage.setItem("ko_lastquote", JSON.stringify(quote)); nav("/account"); }} className="btn-gold" data-testid="payment-goto-account">Create Account / Sign In</button>
      </div>
    );
  }

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments", {
        quote_id: quote.quote_id || "", plan: selected, amount: current.amount,
        estimated_total: total, event_date: quote.event_date || "", event_type: quote.event_type || "",
      });
      localStorage.removeItem("ko_lastquote");
      setDone(true);
      toast.success("Payment recorded — opening PayPal…");
      if (data.paypal_link) window.open(data.paypal_link, "_blank", "noopener");
    } catch {
      toast.error("Could not process payment. Please try again.");
    } finally { setPaying(false); }
  };

  if (done) return (
    <div className="border border-gold/40 bg-gold/[0.05] p-8 text-center" data-testid="payment-confirmation">
      <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-5" />
      <h3 className="text-2xl font-light text-white mb-3">Payment Confirmation</h3>
      <p className="text-white/60 max-w-md mx-auto mb-2">We've recorded your <span className="text-gold">{current.label}</span> of <span className="text-gold">${current.amount.toFixed(2)}</span>.</p>
      <p className="text-white/45 text-sm max-w-md mx-auto mb-6">Complete the payment in the PayPal window. A confirmation email is on its way, and we'll verify and reserve your date shortly.</p>
      <button onClick={() => nav("/account")} className="btn-outline" data-testid="payment-view-account">View My Payments</button>
    </div>
  );

  return (
    <div className="border border-white/10 p-8" data-testid="payment-panel">
      <h3 className="text-2xl font-light text-white mb-2">Secure Your Booking</h3>
      <p className="text-white/50 text-sm mb-6">Estimated event total: <span className="text-gold">${total.toFixed(2)}</span>. Choose a payment option:</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {plans.map((p) => (
          <button key={p.id} onClick={() => setSelected(p.id)} data-testid={`plan-${p.id}`}
            className={`text-left p-5 border transition-colors ${selected === p.id ? "border-gold bg-gold/10" : "border-white/15 hover:border-gold/50"}`}>
            <div className="flex items-center justify-between">
              <span className="text-white">{p.label}</span>
              <span className="text-gold font-serif text-lg">${p.amount.toFixed(2)}</span>
            </div>
            <p className="text-white/45 text-xs mt-1">{p.note}</p>
          </button>
        ))}
      </div>
      <button onClick={pay} disabled={paying} className="btn-gold w-full" data-testid="pay-with-paypal">
        {paying ? "Processing…" : `Pay $${current.amount.toFixed(2)} with PayPal`}
      </button>
      <p className="text-white/35 text-xs text-center mt-3">You'll be redirected to PayPal to complete your payment securely.</p>
    </div>
  );
};

export default PaymentPanel;
